// Offscreen shadow buffer + composite into world canvas
// Renders at 25% resolution for performance.
// Uses transparent-based buffer with dark circles, masked to hex areas
// via destination-in before compositing onto the world canvas.

const SHADOW_SCALE = 0.25;
const SHADOW_OFFSET_X = 20;  // base shadow offset in screen pixels
const SHADOW_OFFSET_Y = 25;
const SHADOW_ENLARGE = 1.15;  // slightly larger than cloud
const COMPOSITE_ALPHA = 0.55; // overall shadow darkness when drawn onto tiles

export class CloudShadowRenderer {
  constructor() {
    this._offscreen = null;
    this._offCtx = null;
    this._w = 0;
    this._h = 0;
  }

  _ensureBuffer(canvasW, canvasH) {
    const w = Math.ceil(canvasW * SHADOW_SCALE);
    const h = Math.ceil(canvasH * SHADOW_SCALE);
    if (this._w !== w || this._h !== h) {
      this._offscreen = document.createElement('canvas');
      this._offscreen.width = w;
      this._offscreen.height = h;
      this._offCtx = this._offscreen.getContext('2d');
      this._w = w;
      this._h = h;
    }
  }

  /**
   * Render shadow shapes into offscreen buffer (transparent + dark circles).
   */
  render(cloudSystem, camera, canvasW, canvasH, nowMs) {
    this._ensureBuffer(canvasW, canvasH);
    const ctx = this._offCtx;
    const s = SHADOW_SCALE;

    // Clear to fully transparent
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, this._w, this._h);

    const timeSec = nowMs / 1000;

    for (let li = 0; li < cloudSystem.layerCount; li++) {
      const visible = cloudSystem.getGroundProjectedClouds(li, camera, canvasW, canvasH);
      const layerHeight = cloudSystem.getHeight(li);
      const heightFactor = layerHeight / 80;
      const shadowOffX = SHADOW_OFFSET_X * heightFactor;
      const shadowOffY = SHADOW_OFFSET_Y * heightFactor;
      // Far clouds cast lighter shadows
      const layerStrength = li === 0 ? 0.4 : 0.7;

      for (const { cloud, sx, sy, scale } of visible) {
        const wobbleX = Math.sin(timeSec * cloud.wobbleSpeed + cloud.wobblePhase) * cloud.wobbleAmp * scale;
        const wobbleY = Math.cos(timeSec * cloud.wobbleSpeed * 0.7 + cloud.wobblePhase) * cloud.wobbleAmp * 0.5 * scale;

        ctx.globalAlpha = layerStrength;

        for (const circle of cloud.circles) {
          const cx = (sx + circle.ox * scale + wobbleX + shadowOffX) * s;
          const cy = (sy + circle.oy * scale + wobbleY + shadowOffY) * s;
          const r = circle.r * scale * SHADOW_ENLARGE * s;

          const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
          grad.addColorStop(0, 'rgba(0,0,0,0.7)');
          grad.addColorStop(0.5, 'rgba(0,0,0,0.35)');
          grad.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    ctx.globalAlpha = 1;
  }

  /**
   * Mask the shadow buffer to hex areas, then composite onto the world canvas.
   * @param {CanvasRenderingContext2D} worldCtx
   * @param {number} canvasW
   * @param {number} canvasH
   * @param {Array} hexScreenPositions - array of {x, y} screen centers
   * @param {number} hexSz - screen hex radius
   * @param {function} hexCornersFn - hexCorners(cx, cy, sz) function
   */
  composite(worldCtx, canvasW, canvasH, hexScreenPositions, hexSz, hexCornersFn) {
    if (!this._offscreen || !hexScreenPositions || hexScreenPositions.length === 0) return;

    const ctx = this._offCtx;
    const s = SHADOW_SCALE;

    // Mask shadow buffer to hex areas using destination-in
    ctx.globalCompositeOperation = 'destination-in';
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    for (const pos of hexScreenPositions) {
      const corners = hexCornersFn(pos.x * s, pos.y * s, hexSz * s);
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let j = 1; j < corners.length; j++) {
        ctx.lineTo(corners[j].x, corners[j].y);
      }
      ctx.closePath();
    }
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';

    // Composite masked shadow buffer onto world canvas
    worldCtx.save();
    worldCtx.globalAlpha = COMPOSITE_ALPHA;
    worldCtx.drawImage(this._offscreen, 0, 0, canvasW, canvasH);
    worldCtx.restore();
  }
}
